import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { 
  wordpressApi, 
  WordPressComPost,
  formatDate, 
  getExcerpt,
  getFeaturedImage
} from "../services/wordpressApi";
import { Logo } from "./Logo";

// Simple animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Utility function to decode HTML entities
function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

function Header() {
  return (
    <div className="relative w-full z-10 mb-12" data-name="Header">
      <div className="box-border content-stretch flex flex-row items-center justify-start p-0 relative w-full">
        <Link to="/" className="block">
          <Logo />
        </Link>
      </div>
    </div>
  );
}

function BlogPostCard({ post }: { post: WordPressComPost }) {
  const featuredImage = getFeaturedImage(post);
  const excerpt = decodeHtmlEntities(getExcerpt(post, 120));

  return (
    <motion.article
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-300"
      variants={itemVariants}
      whileHover={{ y: -2 }}
    >
      <Link to={`/blog/${post.slug}`} className="block">
        {featuredImage && (
          <div className="aspect-video overflow-hidden">
            <img
              src={featuredImage}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2 leading-tight">
            {post.title}
          </h2>
          <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">{excerpt}</p>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <div className="flex items-center space-x-1">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(post.date)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Clock className="w-4 h-4" />
              <span>5 min read</span>
            </div>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}

function BlogHeader() {
  return (
    <motion.div
      className="text-center mb-16"
      variants={itemVariants}
    >
      <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
        Blog
      </h1>
      <p className="text-lg text-gray-600 max-w-2xl mx-auto">
        Insights and strategies for transforming knowledge into powerful documentation
      </p>
    </motion.div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="flex items-center space-x-3">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="text-gray-600">Loading articles...</span>
      </div>
    </div>
  );
}

function ErrorMessage({ message }: { message: string }) {
  return (
    <div className="text-center py-16">
      <div className="text-red-600 mb-4">
        <svg className="w-12 h-12 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Oops! Something went wrong</h3>
      <p className="text-gray-600">{message}</p>
    </div>
  );
}

export function WordPressBlogPage() {
  const [posts, setPosts] = useState<WordPressComPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const postsData = await wordpressApi.getPosts();
      setPosts(postsData);
    } catch (err) {
      setError('Failed to load blog posts. Please try again later.');
      console.error('Error loading posts:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Header />
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <BlogHeader />

          {loading && <LoadingSpinner />}
          
          {error && <ErrorMessage message={error} />}
          
          {!loading && !error && posts.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-600">No blog posts found.</p>
            </div>
          )}

          {!loading && !error && posts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {posts.map((post) => (
                <BlogPostCard key={post.ID} post={post} />
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
} 